import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import AccountSetup from "../../components/AccountSetup";
import InviteTeam from "../../components/InviteTeam";
import ScreenStepper from "../../components/ScreenStepper";
import TeamSize from "../../components/TeamSize";
import { PulseLoader } from 'react-spinners';
import { checkUserEmail, registerUser, setOrganizationTeamSize, inviteUser } from "../../services/api/user-api";
import { UserLogin } from "../../services/api/authentication-api";
import { authorize, login } from "../../redux/auth/auth-actions";
import { authSelector } from "../../redux/auth/selectors";
import { errorNotification } from "../../redux/notifications";
import { decodeJwt } from "jose";
import PropTypes from "prop-types";
import "./Onboarding.scss";

const getFirstName = (fullName) => fullName ? fullName.split(" ")[0] : "";

// Onboarding flags: 0 - no email in DB,  1 - email in DB,
//  2 - email in DB and is admin or super-admin => user drop off behaviour to continue step 2
const Onboarding = ({onLogin, onHide}) => {
    const [initialScreen, setInitialScreen] = useState(0);
    const [checkedEmail, setCheckedEmail] = useState(false);
    const [dropOffToken, setDropOffToken] = useState("0");
    const [accountSetupValidate, setAccountSetupValidate] = useState(false);
    const [accountSetupIsValid, setAccountSetupIsValid] = useState();
    const [fullName, setFullName] = useState();
    const [firstName, setFirstName] = useState();
    const [organization, setOrganization] = useState();
    const [password, setPassword] = useState();

    const [teamSizeValidate, setTeamSizeValidate] = useState(false);
    const [teamSizeIsValid, setTeamSizeIsValid] = useState();
    const [teamSize, setTeamSize] = useState();

    const [inviteTeamValidate, setInviteTeamValidate] = useState(false);
    const [inviteTeamIsValid, setInviteTeamIsValid] = useState();
    const [emails, setEmails] = useState();

    const [authorizeFlag, setAuthorizeFlag] = useState(false);
    const [dispatchLoginOnce, setDispatchLoginOnce] = useState(0);
    const [updatedKey, setUpdatedKey] = useState(1);
    const [isLoading, setIsLoading] = useState(true);

    const history = useHistory();

    const auth = useSelector(authSelector);
    const email = auth.email;
    const orgExists = !!auth.orgId;

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(authorize());
        setAuthorizeFlag(true);
    }, [])

    const fetchCheckUserEmail = async () => {
        setIsLoading(true);
        await checkUserEmail().then(
            async (result) => {
                if(result.data.length > 0) {
                    setDropOffToken(result.data);
                    setCheckedEmail(true);
                } else{
                    setDropOffToken("0");
                    setCheckedEmail(false);
                }
            },
            (error) => {
                dispatch(authorize());
                console.log(error)
                setDropOffToken("0");
                setCheckedEmail(false);
            }
        )
    }

    useEffect(() => {
        if (authorizeFlag) fetchCheckUserEmail();
    }, [authorizeFlag])

    const openTasks = () => {
        let location = {
            pathname: "/tasks",
            state: {
                // Handy if we want to pass state
            },
        };
        history.push(location);
    };

    const validateAccountSetup = (result) => {
        setAccountSetupIsValid(result.isValid);
        setFullName(result.fields.fullName);
        setFirstName(getFirstName(result.fields.fullName));
        setOrganization(result.fields.organization);
        setPassword(result.fields.password);
    };

    const validateTeamSize = (result) => {
        setTeamSizeIsValid(result.isValid);
        setTeamSize(result.teamSize);
    };

    const validateInviteTeam = (result) => {
        setInviteTeamIsValid(result.isValid);
        setEmails(result.emailsSelection);
    };

    const handleAccountSetup = async () => {
        setAccountSetupValidate(true);
        if (accountSetupIsValid) {
            try {
                await registerUser({ fullName, email, password, organization });
            } catch (error) {
                if (error.response?.status === 401) {
                    dispatch(errorNotification({
                        header: "Registration link expired",
                        message: "Please, signup again.",
                    }));
                } else {
                    dispatch(errorNotification({
                        header: "Failed to register user",
                        message: "Contact support at support@triyo.io.",
                    }));
                }
                return false;
            }

            dispatch(login({
                username: email,
                password,
                timezone: new Date().getTimezoneOffset().toString()
            }));

            if (orgExists) {
                await fetchLoginUser();
                onHide();
            }
        }
        return accountSetupIsValid;
    }

    const handleTeamSize = () => {
        setTeamSizeValidate(true);
        return teamSizeIsValid;
    }

    const handleInviteTeam = async () => {
        setInviteTeamValidate(true);
        if (teamSizeIsValid ) {
            try{
                setOrganizationTeamSize({ email, fullName, organization, teamSize });
            }catch(error){
                console.log(error);
            }
        }
        if (inviteTeamIsValid ) {
            try {
                await Promise.all(emails.map(inviteUser));
            } catch (error) {
                dispatch(errorNotification({
                    header: "Failed to invite users",
                    message: "Contact support at support@triyo.io.",
                }));
                return false;
            }
            onHide();
        }
        await fetchLoginUser();
        return inviteTeamIsValid;
    }

    const onNextHandler = async ({ currentScreen }) => {
        switch (currentScreen) {
            case 0:
                return handleAccountSetup();
            case 1:
                return handleTeamSize();
            case 2:
                return handleInviteTeam();
            default:
                return;
        }
    };

    const onPreviousHandler = async () => {
        return false;
    };

    const onSkipHandler = async({ currentScreen })=>{
        if(currentScreen === 2){
            await fetchLoginUser();
            onHide();
        }
    }

    const fetchLoginUser = async() => {
        let currDateOffset = (new Date()).getTimezoneOffset();
        let loginObj = {username: email, password: password, browserTime: currDateOffset};
        await UserLogin(loginObj).then(
            async (result) => {
                if(result.token != null) onLogin(result.token);
            },
            (error) => {
                console.log(error);
            }
        )
    }

    // Here we handle the logic for user drop-off
    useEffect(() => {
        if (dropOffToken?.length > 1) { // No drop-off token we don't enter here
            setIsLoading(true);
            if (!firstName && !password) {
                let userJWT = decodeJwt(dropOffToken)
                if (userJWT.onboardingFlag === 1) {
                    openTasks();
                } else if (userJWT.onboardingFlag === 2) {
                    setPassword(userJWT.password);
                    setFullName(userJWT.name);
                    setFirstName(getFirstName(userJWT.name));
                    setOrganization(userJWT.orgName);
                }
            }

            if (password?.length > 0 && dispatchLoginOnce === 0) { // Preventing dispatching login "n" times for 1 sec
                dispatch(login({
                    username: email,
                    password: password,
                    timezone: new Date().getTimezoneOffset().toString()
                }));
                setDispatchLoginOnce(1);
            }

            if (firstName?.length > 0) {
                if (updatedKey === 1) { // This is going to prevent unnecessary re-renders
                    setTimeout(() => {
                        let newKey;
                        setInitialScreen(1);
                        newKey = updatedKey + 1;
                        setUpdatedKey(newKey);
                        setIsLoading(false);
                    }, 1000)
                }
            }
        } else {
            if (dropOffToken==="0") setIsLoading(false);
        }
    }, [dropOffToken, firstName, password]);

    return (
        <div className = "onboarding">
            {
                isLoading &&
                <div className = "pulse-loader">
                    <PulseLoader></PulseLoader>
                </div>
            }
            {
                !isLoading && (updatedKey > 1 || dropOffToken === "0") &&
                <ScreenStepper key={updatedKey}
                    initialScreen={initialScreen}
                    heading={orgExists ? "Account setup" : "Set up your organization"}
                    onNext={onNextHandler}
                    onPrevious={onPreviousHandler}
                    onSkip={onSkipHandler}
                    hideSkip={orgExists}
                    hidePrevious={orgExists}
                    hideSteps={orgExists}
                    onHide={onHide}
                >
                    <AccountSetup
                        stepperLabel="Account setup"
                        validate={accountSetupValidate}
                        onValidate={validateAccountSetup}
                    />
                    {!orgExists &&
                        <TeamSize
                            stepperLabel="Team size"
                            validate={teamSizeValidate}
                            onValidate={validateTeamSize}
                            firstName={firstName}
                            teamSize={teamSize}
                        />
                    }
                    {!orgExists &&
                        <InviteTeam
                            stepperLabel="Invite your team"
                            canSkip={true}
                            validate={inviteTeamValidate}
                            onValidate={validateInviteTeam}
                            firstName={firstName}
                            emails={emails}
                        />
                    }
                </ScreenStepper>
            }
        </div>
    );
}

Onboarding.propTypes = {
    onLogin: PropTypes.func,
    onHide: PropTypes.func,
}

export default Onboarding;